import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import AlertModal from "../components/Shared/AlertModal/AlertModal";
import { ErrorConstants } from "../constants";
import useAlert from "../hooks/useAlert";
import useSingleRequest from "../hooks/useSingleRequest";
import { IBaseEcg } from "../models/base-ecg.interface";
import { IAction, IEcg, IEcgDetails, IFindings } from "../models/ecg.interface";
import ecgsService from "../services/ecgs.service";
import logger from "../utils/logger";

export type EcgEdit = {
  findings: IFindings[] | undefined;
  actions: IAction[] | undefined;
  interpretation?: string;
  isReadable?: boolean;
  isUrgent?: boolean;
  actionsReviewNote?: string;
};

interface IEcgDetailsState {
  patientId: string | null;
  selectedEcg: IEcg | null;
  ecgEdit: EcgEdit | null;
  ecgEditSnapshot: string | null;
  compareEcg: IEcgDetails | null;
  loading: boolean;
  draft: {
    show: boolean;
    mode?: "read" | "edit";
  };
  saveFailed: boolean;
}

export enum EcgDetailsActionType {
  SET_SELECTED_PATIENT = "SET_SELECTED_PATIENT",
  SET_SELECTED_ECG_DETAILS = "SET_SELECTED_ECG_DETAILS",
  SET_ECG_EDIT = "SET_ECG_EDIT",
  SET_ECG_EDIT_SNAPSHOT = "SET_ECG_EDIT_SNAPSHOT",
  SET_SELECTED_COMPARE_ECG = "SET_SELECTED_COMPARE_ECG",
  SET_LOADING = "SET_LOADING",
  SET_DRAFT = "SET_DRAFT",
  SET_SAVE_FAILED = "SET_SAVE_FAILED",
}

type EcgDetailsReducerAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_SELECTED_COMPARE_ECG"; payload: IEcgDetails | null }
  | { type: "SET_SELECTED_ECG_DETAILS"; payload: IEcg | null }
  | { type: "SET_ECG_EDIT"; payload: EcgEdit | null }
  | { type: "SET_ECG_EDIT_SNAPSHOT"; payload: string | null }
  | { type: "SET_SELECTED_PATIENT"; payload: string | null }
  | {
      type: "SET_DRAFT";
      payload: {
        show: boolean;
        mode?: "read" | "edit";
      };
    }
  | { type: "SET_SAVE_FAILED"; payload: boolean };

type EcgDetailsActions = {
  loadDetails: () => Promise<void>;
  edit: (ecgEdit: EcgEdit) => void;
  isEdited: (opts?: { includeUrgent?: boolean }) => boolean;
};

interface IEcgDetailsContext {
  state: IEcgDetailsState;
  dispatch: Dispatch<EcgDetailsReducerAction>;
  actions: EcgDetailsActions;
}

const ecgDetailsInitState: IEcgDetailsState = {
  compareEcg: null,
  selectedEcg: null,
  ecgEdit: {
    actions: [],
    findings: [],
    actionsReviewNote: "",
  },
  ecgEditSnapshot: null,
  loading: false,
  patientId: null,
  draft: {
    show: false,
  },
  saveFailed: false,
};

const EcgDetailsContext = React.createContext<IEcgDetailsContext>({
  state: ecgDetailsInitState,
  dispatch: () => {},
  actions: {
    loadDetails: () => new Promise((resolve) => resolve()),
    edit: () => {},
    isEdited: () => false,
  },
});

const ecgDetailsReducer = (
  state: IEcgDetailsState,
  action: EcgDetailsReducerAction
): IEcgDetailsState => {
  switch (action.type) {
    case EcgDetailsActionType.SET_SELECTED_PATIENT:
      return { ...state, patientId: action.payload };
    case EcgDetailsActionType.SET_SELECTED_ECG_DETAILS:
      return { ...state, selectedEcg: action.payload };
    case EcgDetailsActionType.SET_ECG_EDIT:
      return { ...state, ecgEdit: action.payload };
    case EcgDetailsActionType.SET_ECG_EDIT_SNAPSHOT:
      return { ...state, ecgEditSnapshot: action.payload };
    case EcgDetailsActionType.SET_SELECTED_COMPARE_ECG:
      return { ...state, compareEcg: action.payload };
    case EcgDetailsActionType.SET_LOADING:
      return { ...state, loading: action.payload };
    case EcgDetailsActionType.SET_DRAFT:
      return { ...state, draft: action.payload };
    case EcgDetailsActionType.SET_SAVE_FAILED:
      return { ...state, saveFailed: action.payload };
    default:
      return state;
  }
};

type EcgDetailsProviderProps = {
  selectedEcg: IBaseEcg | null;
  patientId: string | null;
};
export const EcgDetailsContextProvider = ({
  selectedEcg,
  patientId,
  children,
}: PropsWithChildren<EcgDetailsProviderProps>) => {
  const [state, dispatch] = useReducer(ecgDetailsReducer, {
    ...ecgDetailsInitState,
    selectedEcg: selectedEcg,
  });

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const { isCanceled, getCancelToken, disposeToken } = useSingleRequest(
    EcgDetailsContextProvider.name
  );

  useEffect(() => {
    if (!patientId) return;
    dispatch({
      type: EcgDetailsActionType.SET_SELECTED_PATIENT,
      payload: patientId,
    });
  }, [patientId]);

  useEffect(() => {
    dispatch({
      type: EcgDetailsActionType.SET_SELECTED_COMPARE_ECG,
      payload: null,
    });
    dispatch({
      type: EcgDetailsActionType.SET_DRAFT,
      payload: { show: false },
    });
    dispatch({
      type: EcgDetailsActionType.SET_SAVE_FAILED,
      payload: false,
    });

    actions.loadDetails();
    // eslint-disable-next-line
  }, [selectedEcg?.id]);

  const actions: EcgDetailsActions = {
    isEdited: (opts?: { includeUrgent?: boolean }) => {
      const { selectedEcg, ecgEdit } = state;

      if (!selectedEcg || !ecgEdit) return false;

      const options = opts ?? { includeUrgent: true };

      const src: EcgEdit = {
        actions: selectedEcg.actions ?? [],
        findings: selectedEcg.findings ?? [],
        isReadable: selectedEcg.isReadable,
      };

      const dest: EcgEdit = {
        actions: ecgEdit.actions ?? [],
        findings: ecgEdit.findings ?? [],
        isReadable: ecgEdit.isReadable,
        interpretation: ecgEdit.interpretation ?? undefined,
      };

      if (options.includeUrgent) {
        src.isUrgent = selectedEcg.isUrgent;
        dest.isUrgent = ecgEdit.isUrgent;
      }

      return JSON.stringify(src) !== JSON.stringify(dest);
    },
    edit: (ecgEdit: EcgEdit) => {
      dispatch({
        type: EcgDetailsActionType.SET_ECG_EDIT,
        payload: ecgEdit,
      });

      const { show, mode } = state.draft;
      if (mode === "edit" && show) return;

      dispatch({
        type: EcgDetailsActionType.SET_DRAFT,
        payload: {
          show: true,
          mode: "edit",
        },
      });
    },
    loadDetails: async () => {
      const { id: cancelTokenId, cancelToken } = getCancelToken();

      try {
        dispatch({
          type: EcgDetailsActionType.SET_LOADING,
          payload: true,
        });

        dispatch({
          type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
          payload: null,
        });
        dispatch({
          type: EcgDetailsActionType.SET_ECG_EDIT,
          payload: null,
        });
        dispatch({
          type: EcgDetailsActionType.SET_ECG_EDIT_SNAPSHOT,
          payload: null,
        });

        if (!selectedEcg?.id) {
          logger.error("Error loading ECG details. No ECG is selected");
          return;
        }

        const { success, data, errorCode } = await ecgsService.getEcgDetails({
          id: selectedEcg?.id,
          cancelToken,
        });

        if (isCanceled(cancelTokenId)) {
          return;
        }

        if (errorCode === ErrorConstants.ECG_REVIEW_LOCKED) {
          const { message, support } = getAlertMessageAndSupportContent(
            "ECG.REVIEW.LOCKED",
            "The ECG you are trying to load is already in review by another Reviewer.",
            {},
            errorCode
          );
          showErrorAlert(message, support);
          return;
        }

        if (!success || !data) {
          const { message, support } = getAlertMessageAndSupportContent(
            "ERROR.LOADING_ECG",
            "An error occurred while loading ECG data.<br></br>Please try again.",
            {
              br: () => <br></br>,
            },
            errorCode
          );
          showErrorAlert(message, support);
          return;
        }

        const { ecgDetails } = data;

        const {
          patient,
          date,
          hasInterpretations,
          organization,
          unviewedEcg,
          isApproved,
          archiving,
          isArchived,
        } = selectedEcg;

        const ecgPayload: IEcg = {
          ...ecgDetails,
          date,
          patient,
          hasInterpretations,
          organization,
          unviewedEcg,
          isApproved,
          archiving,
          isArchived,
        };

        dispatch({
          type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
          payload: ecgPayload,
        });

        const ecgEditPayload: EcgEdit = {
          actions: ecgPayload.actions?.map((a) => ({ ...a })) ?? [],
          actionsReviewNote: ecgPayload.actionsReviewNote,
          findings:
            ecgPayload.findings
              ?.filter((f) => f.isEditable)
              .map((f) => ({ ...f })) ?? [],
          isUrgent: ecgPayload.isUrgent,
          isReadable: ecgPayload.isReadable,
        };

        dispatch({
          type: EcgDetailsActionType.SET_ECG_EDIT,
          payload: ecgEditPayload,
        });
      } finally {
        // TODO: Try extracting this code to be part of a flow of the hook useSingleRequest hook
        if (!isCanceled(cancelTokenId)) {
          /**
           * If the request has not been canceled,
           * it means that the request was fulfilled,
           * and we can set loading to false
           */
          dispatch({
            type: EcgDetailsActionType.SET_LOADING,
            payload: false,
          });
        }

        disposeToken(cancelTokenId);
      }
    },
  };

  return (
    <EcgDetailsContext.Provider value={{ state, dispatch, actions }}>
      {children}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </EcgDetailsContext.Provider>
  );
};
export const useEcgDetailsContext = () => useContext(EcgDetailsContext);
